import { revokePrivateSession } from './components/MomentReview/momentsSessionClient';
// App.js
import React, { useState, useEffect } from 'react';
import checkEnvironmentVariables from './utils/envCheck';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
// Use the configured API instance instead of direct axios import
import { api, getApiUrl } from './utils/apiConfig';
// Already imported above

// Components
import Login from './components/Login';
import Chat from './components/Chat';
import StatusPage from './components/StatusPage';
import AdminPage from './components/AdminPage';
import NewLayout from './components/NewLayout';
import CampaignCreation from './components/CampaignCreation';
import CampaignStarterV3 from './components/CampaignStarterV3/CampaignStarterV3';
import CampaignLaunchWorkflow from './components/CampaignLaunchWorkflow/CampaignLaunchWorkflow';
import CopyCampaignWorkflow from './components/CopyCampaignWorkflow/CopyCampaignWorkflow';
import UpdateCreativeWorkflow from './components/UpdateCreativeWorkflow/UpdateCreativeWorkflow';
import PacingCreation from './components/PacingCreation';
import PacingPage from './components/PacingPage';
import PacingDetails from './components/PacingDetails';
import BulkAutoPacingDetails from './components/BulkAutoPacingDetails';
import FoldersPage from './components/FoldersPage';
import CampaignsPage from './components/CampaignsPage';
import CampaignGridPage from './components/CampaignGridPage';
import PerfGridPage from './components/PerfGridPage';
import PerformancePage from './components/Performance/PerformancePage';
import LaunchPage from './components/LaunchPage';
import LaunchCampaignDetails from './components/LaunchCampaignDetails';
import CampaignDetailPage from './components/CampaignDetailPage';
import CampaignPlanReviewPage from './components/CampaignPlanReviewPage';
import MediaPlansPage from './components/MediaPlansPage';
import MediaPlanCreation from './components/MediaPlanCreation';
import MediaPlanDetails from './components/MediaPlanDetails';
import OAuthPage from './components/OAuthPage';
import CreativeManagerPage from './components/CreativeManagerPage';
import CreativeManagerCreation from './components/CreativeManagerCreation';
import CreativeManagerDetails from './components/CreativeManagerDetails';
import CampaignQAPage from './components/CampaignQAPage';
import FrequenceTrackingPage from './components/frequence/FrequenceTrackingPage';
import CampaignQACreation from './components/CampaignQACreation';
import CampaignQADetails from './components/CampaignQADetails';

// Utils
import { canSendMessage, isAdmin } from './utils/permissions';
import { USER_STORAGE_KEY } from './utils/userUtils';
import './utils/intervalTracker'; // Import interval tracker for debugging
import './utils/pollingRegistry'; // Import polling registry for cleanup

// Account Context
import { AccountProvider, useAccount } from './contexts/AccountContext';
import { useAccountSettings } from './hooks/useAccountSettings';

const OffersPage = React.lazy(() => import('./components/Offers/OffersPage'));

const OptimizationWorkspace = React.lazy(() => import('./components/OptimizationWorkspace/OptimizationWorkspace'));

// /campaign/v3/start: campaigns opened from the Launch board (`from=launch`)
// render the campaign-details variant, which picks plan-review vs DSP-reconcile
// from the campaign's stage. `frommock=demo` forces the design-mock demo
// dataset. Every other entry point gets the full Campaign Launch Workflow.
const CampaignStartSwitch = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const useDetails = params.get('from') === 'launch' || params.has('frommock');
  return useDetails ? <LaunchCampaignDetails /> : <CampaignLaunchWorkflow />;
};

// Component to handle root route content based on UI experience
const RootContent = ({ selectedChat, currentChatId, onChatHistoryChange, readOnly, user }) => {
  const navigate = useNavigate();
  const [useNewExperience, setUseNewExperience] = useState(() => {
    const saved = localStorage.getItem('useNewExperience');
    // MUST match NewLayout's default (NewLayout.js) — absent key means NEW.
    // These two read one flag and disagreed on its default, so a profile that
    // had never touched the toggle got the new sidebar with the CLASSIC chat
    // inside it. `/` is also the floor of every history stack (the logged-in
    // /login redirect replaces rather than pushes), so navigating far enough
    // backwards always bottomed out here and "somehow" surfaced Classic UI
    // (demo 0820).
    return saved !== null ? saved === 'true' : true;
  });

  // Listen for localStorage changes to update the state
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('useNewExperience');
      setUseNewExperience(saved === 'true');
    };

    const handleUIExperienceChange = () => {
      handleStorageChange();
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom event for immediate updates within the same tab
    window.addEventListener('uiExperienceChanged', handleUIExperienceChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('uiExperienceChanged', handleUIExperienceChange);
    };
  }, []);

  // If using new experience, show empty state instead of chat
  if (useNewExperience) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Welcome to the New Experience</h2>
          <p className="text-gray-500 mb-6">Get started by creating a new campaign or managing your existing ones.</p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/campaign/new')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Create Campaign
            </button>
            <button
              onClick={() => navigate('/campaigns')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              View Campaigns
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Classic experience - show chat
  return (
    <Chat
      className="h-full"
      selectedChat={selectedChat}
      currentChatId={currentChatId}
      onChatHistoryChange={onChatHistoryChange}
      readOnly={readOnly}
      user={user}
    />
  );
};

const AppContent = () => {
  const [user, setUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true); // Add loading state
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatHistoryVersion, setChatHistoryVersion] = useState(0);
  const [currentChatId, setCurrentChatId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if there's a selectedChat in the location state (coming from StatusPage)
    if (location.state && location.state.selectedChat) {
      const chat = location.state.selectedChat;
      setSelectedChat(chat);
      setCurrentChatId(chat.id);
      // Clear the location state to prevent re-selecting on refresh
      navigate('/', { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    const checkStoredUser = () => {
      try {
        const storedUser = localStorage.getItem(USER_STORAGE_KEY);

        if (!storedUser) {
          setIsLoadingUser(false); // No user found, stop loading
          return;
        }

        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && parsedUser.expiresAt) {
          if (new Date().getTime() < parsedUser.expiresAt) {
            setUser(parsedUser);
            setIsLoadingUser(false); // User loaded successfully
          } else {
            localStorage.removeItem(USER_STORAGE_KEY);
            setIsLoadingUser(false); // Expired user, stop loading
          }
        } else {
          localStorage.removeItem(USER_STORAGE_KEY);
          setIsLoadingUser(false); // Invalid user data, stop loading
        }
      } catch (error) {
        console.error('Error checking stored user:', error);
        // If there's an error parsing the stored user data, clear it
        localStorage.removeItem(USER_STORAGE_KEY);
        setIsLoadingUser(false); // Error occurred, stop loading
      }
    };

    // Check stored user on mount
    checkStoredUser();

    // Add storage event listener to handle changes in other tabs
    const handleStorageChange = (e) => {
      if (e.key === USER_STORAGE_KEY) {
        checkStoredUser();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleLoginSuccess = async (userData) => {
    try {
      // Clear any existing session data
      sessionStorage.clear();

      const expiresAt = new Date().getTime() + (3 * 24 * 60 * 60 * 1000); // 3 days from now
      const userWithExpiration = { ...userData, expiresAt };

      // Check if user exists or create a new user
      try {
        const response = await api.post(getApiUrl('/api/users/login'), {
          google_id: userData.sub,
          email: userData.email,
          display_name: userData.name,
          picture_url: userData.picture
        });

        // Combine backend user data with Google user data
        const combinedUserData = { ...userWithExpiration, ...response.data };

        // Store user data
        try {
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(combinedUserData));
        } catch (error) {
          console.error('Error storing user data:', error);
        }

        // Update the user state
        setUser(combinedUserData);

        // Dispatch a custom event to notify other components about the login
        const loginEvent = new CustomEvent('userLogin', {
          detail: { user: combinedUserData }
        });
        window.dispatchEvent(loginEvent);

        // Force a page reload to ensure all components are reset with the new user
        // This ensures a clean state for all components
        window.location.href = '/';
      } catch (error) {
        console.error('Error during login:', error);

        // Handle 403 Forbidden (user not in allowlist)
        if (error.response && error.response.status === 403) {
          alert(error.response.data.message || 'Access denied. You are not authorized to use this application.');
          handleLogout(); // Sign out the user
        } else {
          alert('An error occurred during login. Please try again later.');
        }
      }
    } catch (error) {
      console.error('Error during login process:', error);
      alert('An unexpected error occurred. Please try again later.');
    }
  };

  const handleLogout = async () => {
    try { await revokePrivateSession(user?.id); } catch { window.alert('Signed out locally. Private-session revocation could not be confirmed; it expires within one hour.'); }
    localStorage.setItem('momentsSessionReset', String(Date.now()));
    try {
      // Clear user data from localStorage
      localStorage.removeItem(USER_STORAGE_KEY);

      // Clear user and selected chat from state
      setUser(null);
      setSelectedChat(null); // Clear selected chat on logout

      // Reset the current chat ID
      setCurrentChatId(null);

      // Clear any session storage that might be used for state persistence
      sessionStorage.clear();

      // Dispatch a custom event to notify other components about the logout
      const logoutEvent = new CustomEvent('userLogout');
      window.dispatchEvent(logoutEvent);

      // Force a page reload to ensure all components are reset
      // This is a more reliable way to reset the application state
      window.location.href = '/';
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleImpersonateUser = async (targetUserId) => {
    try {
      await revokePrivateSession(user?.id);
      localStorage.setItem('momentsSessionReset', String(Date.now()));
      const response = await api.get(getApiUrl(`/api/admin/impersonate/${targetUserId}`), {
        params: { userId: user.id }
      });

      // Ensure the impersonated user data has the expiresAt field
      // Copy it from the current user's data to prevent session expiration on refresh
      const impersonatedUserData = {
        ...response.data,
        expiresAt: user.expiresAt, // Preserve the expiration time from the admin user
        isImpersonating: true, // Flag to indicate this is an impersonated session
        impersonatedBy: user.id, // Store the original user's ID
        // Ensure we have a consistent userId field
        userId: response.data.id || response.data.userId
      };

      console.log('Impersonated user data:', impersonatedUserData);

      // Store the impersonated user data in localStorage
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(impersonatedUserData));

      // Update the user state
      setUser(impersonatedUserData);

      // Refresh chat history to show the impersonated user's chats
      setChatHistoryVersion(v => v + 1);

      // Reset the current chat ID
      setCurrentChatId(null);

      // Reset the selected chat
      setSelectedChat(null);

      // Dispatch a custom event to notify other components about the impersonation
      const impersonationEvent = new CustomEvent('userImpersonation', {
        detail: { impersonatedUser: impersonatedUserData }
      });
      window.dispatchEvent(impersonationEvent);

      // Show notification
      alert(`You are now impersonating ${impersonatedUserData.display_name || impersonatedUserData.email}. Their permissions and access have been applied to your session.`);

      // Navigate to home page
      navigate('/');
    } catch (error) {
      console.error('Error during impersonation:', error);
      alert('Failed to impersonate user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEndImpersonation = async () => {
    try {
      if (!user.impersonatedBy) {
        alert('You are not currently impersonating anyone.');
        return;
      }

      // We no longer clear session storage to preserve the selectedAccountId
      // Only clear specific items if needed
      // sessionStorage.removeItem('specific-key');

      const response = await api.get(getApiUrl('/api/admin/end-impersonation'), {
        params: { impersonatedBy: user.impersonatedBy }
      });

      // Ensure the original admin user data has the expiresAt field
      const originalUserData = {
        ...response.data,
        expiresAt: user.expiresAt // Preserve the expiration time
      };

      // Store the original user data in localStorage
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(originalUserData));

      // Update the user state
      setUser(originalUserData);

      // Refresh chat history to show the original user's chats
      setChatHistoryVersion(v => v + 1);

      // Reset the current chat ID
      setCurrentChatId(null);

      // Reset the selected chat
      setSelectedChat(null);

      // Dispatch a custom event to notify other components about ending impersonation
      const endImpersonationEvent = new CustomEvent('endUserImpersonation', {
        detail: { originalUser: originalUserData }
      });
      window.dispatchEvent(endImpersonationEvent);

      // Redirect to admin page
      navigate('/admin');

      // Show notification
      alert('You have returned to your Super Admin account.');

      // Instead of forcing a page reload, we'll use the custom event system
      // This will allow components to properly handle the end of impersonation
      // without losing their state or causing race conditions
      navigate('/admin');
    } catch (error) {
      console.error('Error ending impersonation:', error);
      alert('Failed to end impersonation: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleChatHistoryChange = (newChatId, forceRefresh = false) => {
    if (newChatId) {
      // First update the current chat ID
      setCurrentChatId(newChatId);

      // If this is a new chat or different chat, update selection
      if (!selectedChat || selectedChat.id !== newChatId) {
        setSelectedChat({ id: newChatId });
      }

      // Always refresh chat history for new chats or when force refresh is requested
      // This ensures the sidebar is updated even when an account selector is present
      if (forceRefresh || !selectedChat || selectedChat.isNew) {
        console.log('Forcing chat history refresh');
        // Use setTimeout to ensure the chat ID is updated before refreshing
        setTimeout(() => {
          setChatHistoryVersion(v => v + 1);
        }, 200);
      } else {
        // For existing chats, just refresh normally
        setChatHistoryVersion(v => v + 1);
      }
    }
  };

  const handleNewChat = () => {
    setCurrentChatId(null);
    // Set selectedChat with a flag indicating it's a new chat
    setSelectedChat({ isNew: true });

    // Dispatch a custom event to reset the chat component state
    const resetChatEvent = new CustomEvent('resetChat');
    window.dispatchEvent(resetChatEvent);

    // Increment the chat history version to trigger a refresh
    setChatHistoryVersion(v => v + 1);

    // Get the current location
    const currentPath = location.pathname;

    // If we're not on the home page, navigate there
    if (currentPath !== '/') {
      navigate('/', { state: { fromNewChat: true } });
    }
  };

  const handleChatSelect = (chat) => {
    // Get the current location
    const currentPath = location.pathname;

    // Check if we're selecting the same chat
    const isSameChat = selectedChat && chat && selectedChat.id === chat.id;

    // If we're selecting a different chat, update the state
    if (!isSameChat) {
      setSelectedChat(chat);
      setCurrentChatId(chat ? chat.id : null);
    }

    // Navigate to home page when a chat is selected
    // If we're coming from a different page or selecting a different chat, add a flag to the state
    if (currentPath !== '/' || !isSameChat) {
      navigate('/', { state: { fromOtherPage: true } });
    }
  };

  // Show loading screen while checking for stored user
  if (isLoadingUser) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div>Loading...</div>
        <div style={{ fontSize: '0.875rem', color: '#666' }}>Checking authentication status</div>
      </div>
    );
  }

  return (
    <div>
      <Routes>
        <Route
          path="/login"
          element={
            !user ? (
              <Login onLoginSuccess={handleLoginSuccess} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/status"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <StatusPage user={user} />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/admin"
          element={
            user && isAdmin(user) ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <AdminPage
                  user={user}
                  onImpersonateUser={handleImpersonateUser}
                />
              </NewLayout>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/folders"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <FoldersPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaigns"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CampaignsPage
                  onStartCopyCampaign={(preselectedCampaignIds) => navigate(
                    `/campaign/copy${preselectedCampaignIds?.length ? `?campaignIds=${preselectedCampaignIds.join(',')}` : ''}`,
                  )}
                />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaigns/:campaignId/update-creative"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <UpdateCreativeWorkflow />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaign/copy"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CopyCampaignWorkflow />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaign-grid"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CampaignGridPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/optimization-workspace"
          element={user ? <React.Suspense fallback={<div role="status">Opening optimization workspace…</div>}>
            <OptimizationWorkspace />
          </React.Suspense> : <Navigate to="/login" replace />}
        />
        <Route
          path="/performance/:campaignId?"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <PerformancePage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/offers"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <React.Suspense fallback={<div role="status">Opening Offers…</div>}><OffersPage user={user} /></React.Suspense>
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/perf-grid"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <PerfGridPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/launch"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <LaunchPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaigns/:campaignId"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CampaignDetailPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaigns/:campaignId/plan-review"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CampaignPlanReviewPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaign/new"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CampaignCreation />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaign/v3/start"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CampaignStartSwitch />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/pacing"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <PacingPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/pacing/new"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <PacingCreation />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/pacing/bulk/:id"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <BulkAutoPacingDetails />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/pacing/:id"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <PacingDetails />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/media-plans"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <MediaPlansPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/media-plans/new"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <MediaPlanCreation />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/media-plans/:id"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <MediaPlanDetails />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/creative-manager"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CreativeManagerPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/creative-manager/new"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CreativeManagerCreation />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/creative-manager/:id"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CreativeManagerDetails />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaign-qa"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CampaignQAPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaign-qa/new"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CampaignQACreation />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/campaign-qa/:id"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <CampaignQADetails />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/frequence"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <FrequenceTrackingPage />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/oauth"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <OAuthPage user={user} />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/"
          element={
            user ? (
              <NewLayout
                user={user}
                onLogout={handleLogout}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
                chatHistoryVersion={chatHistoryVersion}
                selectedChat={selectedChat}
                readOnly={!canSendMessage(user)}
                onEndImpersonation={handleEndImpersonation}
              >
                <RootContent
                  selectedChat={selectedChat}
                  currentChatId={currentChatId}
                  onChatHistoryChange={handleChatHistoryChange}
                  readOnly={!canSendMessage(user)}
                  user={user}
                />
              </NewLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </div>
  );
};

// The AccountContextRegistrar has been removed since we no longer need to register with the API config

function App() {
  // Check environment variables on app initialization
  useEffect(() => {
    checkEnvironmentVariables();
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Router>
        <AccountProvider>
          <AppContent />
        </AccountProvider>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
